import { gql, useMutation } from '@apollo/client'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  FormHelperText,
} from '@mui/material'
import { Brand, ContentRequest } from '@promoboxx/graphql-gateway-types'
import { Field, Formik } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import Analytics from '@src/analytics/Analytics'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import {
  brandsForCurrentRetailerAccountSelector,
  retailerIdsByBrandIdSelector,
} from '@src/stores/app/selectors'

const BULK_CREATE_CONTENT_REQUEST = gql`
  mutation bulkContentRequestsCreation(
    $fields: [String!]
    $campaign_requests: [UpsertContentRequestInput!]
  ) {
    upsertContentRequests(
      fields: $fields
      campaign_requests: $campaign_requests
    ) {
      id
      brand {
        id
      }
    }
  }
`
interface RequestMoreCampaignsForm {
  isAllBrandsSelected: boolean
  selectedBrandIds: Brand['id'][]
  desiredContentTypes: string[]
  desiredContentFrequency: string
  desiredSocialChannels: string[]
  desiredSocialChannelsOther: string
  contentRequests: ContentRequest[]
  notes: string
}

const requestMoreContentValidationSchema = Yup.object().shape({
  notes: Yup.string()
    .required()
    .min(20)
    .label(I18n.t('requestMoreCampaigns.note.label')),
  desiredSocialChannelsOther: Yup.string().when('desiredSocialChannels', {
    is: (val: string[]) => val.includes('Other'),
    then: Yup.string()
      .required()
      .label(
        I18n.t('requestMoreCampaigns.desiredSocialChannels.options.other'),
      ),
  }),
  selectedBrandIds: Yup.array().when('isAllBrandsSelected', {
    is: (val: boolean) => val === false,
    then: Yup.array()
      .required()
      .min(1)
      .label(I18n.t('requestMoreCampaigns.selectBrands')),
  }),
})

const fieldTranslationMap = {
  desiredContentTypes: [
    { key: 'product', value: 'Product' },
    { key: 'promotional', value: 'Promotional' },
    { key: 'inspirational', value: 'Inspirational' },
    { key: 'educational', value: 'Educational' },
    { key: 'automated', value: 'Automated' },
    { key: 'paid', value: 'Paid' },
  ],
  desiredContentFrequency: [
    { key: 'twoWeeks', value: '0-2 Weeks' },
    { key: 'threeWeeks', value: '3-4 Weeks' },
    { key: 'monthly', value: 'Monthly' },
  ],
  desiredSocialChannels: [
    { key: 'facebook', value: 'Facebook' },
    { key: 'twitter', value: 'Twitter' },
    { key: 'instagram', value: 'Instagram' },
    { key: 'downloadables', value: 'Downloadables' },
    { key: 'email', value: 'Email' },
    { key: 'webBanners', value: 'Web Banners' },
    { key: 'other', value: 'Other' },
  ],
}

const RequestCampaignsDialog: React.FC<
  React.PropsWithChildren<{
    brandId?: string | null
    onClose?: () => void
    open?: boolean
    total?: number
    preFill?: Partial<RequestMoreCampaignsForm>
  }>
> = ({ brandId, open = false, total, onClose = () => {}, preFill = {} }) => {
  const brands = useSelector(brandsForCurrentRetailerAccountSelector).sort(
    (a: Brand, b: Brand) => a?.name?.localeCompare(b?.name || ''),
  )
  const retailerIdsByBrandId = useSelector(retailerIdsByBrandIdSelector)
  const [displayFormQuestions, setDisplayQuestions] = useState<boolean>(false)
  const [bulkCreateContentRequests] = useMutation(BULK_CREATE_CONTENT_REQUEST)
  const { enqueueSnackbar } = useSnackbar()

  const createBrandIdsArray = (formValues: {
    isAllBrandsSelected: boolean
    selectedBrandIds: string[]
  }) => {
    const brandIds = formValues.isAllBrandsSelected
      ? brands.map((x: Brand) => x.id)
      : formValues.selectedBrandIds
    const retailerIds = []

    for (const brandId of brandIds) {
      const retailerBrandId = retailerIdsByBrandId[brandId]
      if (retailerBrandId) {
        retailerIds.push(retailerBrandId)
      }
    }
    return brandIds
  }
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        isAllBrandsSelected: false,
        selectedBrandIds: brandId ? [brandId] : [],
        desiredContentTypes: Array<string>(),
        desiredContentFrequency: '',
        desiredSocialChannels: Array<string>(),
        desiredSocialChannelsOther: '',
        contentRequests: Array<ContentRequest>(),
        notes: '',
        ...preFill,
      }}
      validationSchema={requestMoreContentValidationSchema}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true)
        const brandIds = createBrandIdsArray(values)

        const desiredSocialChannels = values.desiredSocialChannels.filter(
          (el) => el !== 'Other',
        )
        if (values.desiredSocialChannels.includes('Other'))
          // we may want to scrub the string inserted here to some specified format
          desiredSocialChannels.push(
            `Other:${values.desiredSocialChannelsOther}`,
          )

        const payload = brandIds.map((brandId: number) => {
          return {
            brand_id: brandId,
            retailer_id: retailerIdsByBrandId[brandId],
            desired_content_types: values.desiredContentTypes.join(','),
            desired_content_frequency: values.desiredContentFrequency,
            desired_social_channels: desiredSocialChannels.join(','),
            notes: values.notes,
          }
        })
        try {
          Analytics.track('Click', {
            type: 'Request More Organic Campaigns',
            campaigns: total,
            connectedBrands: brands.length,
            brandIds,
          })

          await bulkCreateContentRequests({
            variables: { fields: ['brand'], campaign_requests: payload },
          })

          enqueueSnackbar(I18n.t('requestMoreCampaigns.successMessage'), {
            variant: 'success',
          })

          actions.resetForm()
          setDisplayQuestions(false)
          onClose()
        } catch (err) {
          console.error(err)
          enqueueSnackbar(I18n.t('requestMoreCampaigns.errorMessage'), {
            variant: 'error',
          })
        }

        actions.setSubmitting(false)
      }}
    >
      {({
        values,
        isValid,
        setFieldValue,
        handleSubmit,
        resetForm,
        isSubmitting,
      }) => {
        const handleClose = () => {
          resetForm()
          setDisplayQuestions(false)
          onClose()
        }

        const handleNextStep = () => {
          setDisplayQuestions(true)
        }

        const handleToggleBrand = (brand: Brand) => {
          let nextValue

          if (values.selectedBrandIds.includes(brand.id)) {
            nextValue = values.selectedBrandIds.filter((x) => x !== brand.id)
          } else {
            nextValue = values.selectedBrandIds.concat(brand.id)
          }
          setFieldValue('selectedBrandIds', nextValue)
        }

        return (
          <Dialog fullWidth maxWidth="xs" onClose={handleClose} open={open}>
            <DialogTitle>{I18n.t('content.requestMore')}</DialogTitle>
            {displayFormQuestions ? (
              <>
                <DialogContent>
                  <Typography>
                    {I18n.t('requestMoreCampaigns.description')}
                  </Typography>
                  <FormHelperText>
                    {I18n.t('requestMoreCampaigns.note.question')}
                  </FormHelperText>
                  <Field
                    component={TextField}
                    fullWidth
                    label={I18n.t('requestMoreCampaigns.note.label')}
                    multiline
                    required
                    name="notes"
                    rows={3}
                    placeholder={I18n.t(
                      'requestMoreCampaigns.note.placeholder',
                    )}
                  />
                  <FormHelperText>
                    {I18n.t(
                      'requestMoreCampaigns.desiredContentTypes.question',
                    )}
                  </FormHelperText>
                  <Field
                    component={TextField}
                    fullWidth
                    label={I18n.t(
                      'requestMoreCampaigns.desiredContentTypes.label',
                    )}
                    name="desiredContentTypes"
                    SelectProps={{ multiple: true }}
                    select
                  >
                    {fieldTranslationMap.desiredContentTypes.map((option) => (
                      <MenuItemWithCheckbox
                        key={option.key}
                        value={option.value}
                      >
                        {I18n.t(
                          `requestMoreCampaigns.desiredContentTypes.options.${option.key}`,
                        )}
                      </MenuItemWithCheckbox>
                    ))}
                  </Field>
                  <FormHelperText>
                    {I18n.t(
                      'requestMoreCampaigns.desiredContentFrequency.question',
                    )}
                  </FormHelperText>
                  <Field
                    component={TextField}
                    fullWidth
                    label={I18n.t(
                      'requestMoreCampaigns.desiredContentFrequency.label',
                    )}
                    name="desiredContentFrequency"
                    select
                  >
                    {fieldTranslationMap.desiredContentFrequency.map(
                      (option) => (
                        <MenuItemWithCheckbox
                          key={option.key}
                          value={option.value}
                        >
                          {I18n.t(
                            `requestMoreCampaigns.desiredContentFrequency.options.${option.key}`,
                          )}
                        </MenuItemWithCheckbox>
                      ),
                    )}
                  </Field>
                  <FormHelperText>
                    {I18n.t(
                      'requestMoreCampaigns.desiredSocialChannels.question',
                    )}
                  </FormHelperText>
                  <Field
                    component={TextField}
                    fullWidth
                    label={I18n.t(
                      'requestMoreCampaigns.desiredSocialChannels.label',
                    )}
                    name="desiredSocialChannels"
                    SelectProps={{ multiple: true }}
                    select
                  >
                    {fieldTranslationMap.desiredSocialChannels.map((option) => (
                      <MenuItemWithCheckbox
                        key={option.key}
                        value={option.value}
                      >
                        {I18n.t(
                          `requestMoreCampaigns.desiredSocialChannels.options.${option.key}`,
                        )}
                      </MenuItemWithCheckbox>
                    ))}
                  </Field>
                  {values.desiredSocialChannels.includes('Other') && (
                    <Field
                      component={TextField}
                      fullWidth
                      label={I18n.t(
                        `requestMoreCampaigns.desiredSocialChannels.options.other`,
                      )}
                      name="desiredSocialChannelsOther"
                    />
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    style={{ marginRight: 'auto' }}
                    onClick={() => setDisplayQuestions(false)}
                  >
                    {I18n.t('shared.back')}
                  </Button>
                  <Button onClick={handleClose}>
                    {I18n.t('shared.cancel')}
                  </Button>
                  <Button
                    color="primary"
                    disabled={isSubmitting || !isValid}
                    onClick={() => handleSubmit()}
                    variant="contained"
                  >
                    {I18n.t('requestMoreCampaigns.submit')}
                  </Button>
                </DialogActions>
              </>
            ) : (
              <>
                <DialogContent>
                  {!brandId && (
                    <>
                      <Typography>
                        {I18n.t('requestMoreCampaigns.selectBrands')}
                      </Typography>
                      <List>
                        <ListItem
                          button
                          divider
                          onClick={() => {
                            setFieldValue(
                              'isAllBrandsSelected',
                              !values.isAllBrandsSelected,
                            )
                          }}
                        >
                          <Checkbox checked={values.isAllBrandsSelected} />
                          <ListItemText primary={I18n.t('nav.allBrands')} />
                        </ListItem>
                        {brands.map((brand: Brand, i: number) => (
                          <ListItem
                            button
                            disabled={values.isAllBrandsSelected}
                            divider={i < brands.length - 1}
                            key={`brand-${brand.name}`}
                            onClick={() => {
                              handleToggleBrand(brand)
                            }}
                          >
                            <Checkbox
                              checked={
                                values.isAllBrandsSelected ||
                                values.selectedBrandIds.includes(brand.id)
                              }
                            />
                            <ListItemText primary={brand.name} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose}>
                    {I18n.t('shared.cancel')}
                  </Button>
                  <Button
                    color="primary"
                    disabled={
                      isSubmitting ||
                      (values.isAllBrandsSelected === false &&
                        values.selectedBrandIds.length === 0)
                    }
                    onClick={handleNextStep}
                    variant="contained"
                  >
                    {I18n.t('shared.next')}
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default RequestCampaignsDialog
